/**
 * VireoHR - Employee Hours & Earnings Screen
 * Ultra-Minimal Design
 */

import { collection, query, where, getDocs } from 'firebase/firestore';
import React, { useState, useEffect } from 'react';

import { Card } from '@/components/Card';
import Layout from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import { firestore } from '@/lib/firebase/client';

interface PayrollData {
  totalHours: number;
  overtimeHours: number;
  finalAmount: number;
  lateCount: number;
  noShowCount: number;
}

export default function HoursEarnings() {
  const { user } = useAuth();
  const [payroll, setPayroll] = useState<PayrollData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.id) {
      fetchPayroll();
    }
  }, [user]);

  const fetchPayroll = async () => {
    if (!user?.id) return;

    try {
      const now = new Date();
      const month = now.getMonth() + 1;
      const year = now.getFullYear();

      const payrollRef = collection(firestore, 'payroll');
      const q = query(
        payrollRef,
        where('employeeId', '==', user.id),
        where('month', '==', month),
        where('year', '==', year)
      );

      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        setPayroll(snapshot.docs[0].data() as PayrollData);
      }
    } catch (error) {
      console.error('Error fetching payroll:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout title="Earnings">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#0D9488] border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  const currentMonth = new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <Layout title="Earnings">
      <div className="p-4 space-y-4">
        <div>
          <h1 className="text-base font-semibold text-[#0D9488]">My Hours & Earnings</h1>
          <p className="text-xs text-[#D4AF37] mt-0.5">{currentMonth}</p>
        </div>

        {payroll ? (
          <>
            {/* Total Earnings */}
            <Card>
              <div className="text-center py-4">
                <p className="text-xs text-[#D4AF37] mb-1">Estimated Earnings</p>
                <p className="text-3xl font-bold text-[#0D9488]">{payroll.finalAmount.toFixed(2)} JD</p>
              </div>
            </Card>

            {/* Hours Breakdown */}
            <Card>
              <h3 className="text-sm font-semibold text-[#0D9488] mb-3">Hours</h3>
              <div className="space-y-2">
                <div className="flex justify-between items-center py-2 border-b border-[#D4AF37]">
                  <span className="text-sm text-[#D4AF37]">Regular Hours</span>
                  <span className="text-sm font-medium text-[#0D9488]">{payroll.totalHours.toFixed(1)} hrs</span>
                </div>
                {payroll.overtimeHours > 0 && (
                  <div className="flex justify-between items-center py-2 border-b border-[#D4AF37]">
                    <span className="text-sm text-[#D4AF37]">Overtime</span>
                    <span className="text-sm font-medium text-[#D4AF37]">{payroll.overtimeHours.toFixed(1)} hrs</span>
                  </div>
                )}
              </div>
            </Card>

            {/* Attendance Summary */}
            <Card>
              <h3 className="text-sm font-semibold text-[#0D9488] mb-3">Attendance</h3>
              <div className="space-y-2">
                <div className="flex justify-between items-center py-2 border-b border-[#D4AF37]">
                  <span className="text-sm text-[#D4AF37]">Late Arrivals</span>
                  <span className="text-sm font-medium text-amber-600">{payroll.lateCount}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-[#D4AF37]">
                  <span className="text-sm text-[#D4AF37]">No-Shows</span>
                  <span className="text-sm font-medium text-red-600">{payroll.noShowCount}</span>
                </div>
              </div>
            </Card>
          </>
        ) : (
          <Card>
            <div className="text-center py-8">
              <p className="text-sm text-[#D4AF37]">No payroll data available</p>
              <p className="text-xs text-[#D4AF37] mt-1">Check back after the first pay period</p>
            </div>
          </Card>
        )}
      </div>
    </Layout>
  );
}
